import { loanBalanceEmpty } from "../defaults/loan-balance-empty";
import { LoanBalance } from "../types/loan-balance";
import { LoanModel } from "../loan.model";
import { useLoanTransactions } from "hooks/loan-transactions/useLoanTransactions";
import { StatementEntryItemType } from "@common/loans/statements/amortization.view-model";
import { AmortizationViewModel } from "@common/loans/statements/amortization.view-model";
import { InterestBalances } from "@common/loans/balances/interest-balances";

function getTotalUnpaidDraws(loan: LoanModel): number {
  return loan.draw_requests
    .filter(draw => draw.status === "unpaid")
    .reduce((acc, curr) => acc + (curr.amount || 0), 0);
}

function getTotalFees(loan: LoanModel): number {
  return loan.fees.reduce((acc, curr) => acc + (curr.fee_amount || 0), 0);
}

function getTotalDraws(loan: LoanModel): number {
  return loan.draw_requests.reduce((acc, curr) => acc + (curr.amount || 0), 0);
}

function getFundedToDate(loan: LoanModel, totalFees: number): number {
  const paidDraws = loan.draw_requests
    .filter(draw => draw.status === "paid")
    .reduce((acc, curr) => acc + (curr.amount || 0), 0);

  return paidDraws + totalFees;
}

function getTotalInterests(amortization: AmortizationViewModel) {
  const interestBalances: InterestBalances = {
    totalInterest: 0,
    totalBaseRate: 0,
    totalDefaultInterest: 0,
  };

  amortization.entries.forEach(entry => {
    entry.items.forEach(item => {
      switch (item.type) {
        case StatementEntryItemType.Interest:
          interestBalances.totalInterest += item.amount;
          break;
        case StatementEntryItemType.BaseRate:
          interestBalances.totalBaseRate += item.amount;
          break;
        case StatementEntryItemType.DefaultInterest:
          interestBalances.totalDefaultInterest += item.amount;
          break;
        default:
          break;
      }
    });
  });

  return interestBalances;
}

function getTotalLoanCost(loan: LoanModel, amortization: AmortizationViewModel): number {
  const field = loan.values.find(value => value.loan_field.label === "Gross Loan Amount");

  if (field) {
    return Number(field?.value?.field ?? 0);
  }

  return amortization.entries.reduce((acc, entry) => acc + (entry.debit || 0), 0) ?? 0;
}

function getRemainingBalance(loanAmount: number, totalDraws: number, totalFees: number): number {
  return loanAmount - totalDraws - totalFees;
}

function getTotalBudget(loan: LoanModel): number {
  return loan.draw_requests.reduce((acc, curr) => acc + (curr.amount || 0), 0);
}

function getProgress(loanAmount: number, remainingBalance: number): number {
  const costAmount = loanAmount - remainingBalance;
  return loanAmount !== 0 ? (costAmount / loanAmount) * 100 : 0;
}

export function useLoanBalance(loan: LoanModel | undefined): LoanBalance {
  const {
    data: amortization,
    isFetching,
    refetch,
  } = useLoanTransactions({
    loan,
    dateRange: {
      start: undefined,
      end: undefined,
    },
  });

  if (!amortization || !loan) {
    return loanBalanceEmpty;
  }

  const loanAmount = Number(loan.amount ?? 0);
  const totalFees = getTotalFees(loan);
  const totalDraws = getTotalDraws(loan);
  const remainingBalance = getRemainingBalance(loanAmount, totalDraws, totalFees);

  return {
    isFetching,
    refetch: async () => {
      await refetch();
    },
    data: {
      progress: getProgress(loanAmount, remainingBalance),
      loanAmount,
      totalBudget: getTotalBudget(loan),
      pendingDraws: getTotalUnpaidDraws(loan),
      totalFees: totalFees,
      fundedToDate: getFundedToDate(loan, totalFees),
      remainingBalance,
      interestBalances: getTotalInterests(amortization),
      totalLoanCost: getTotalLoanCost(loan, amortization),
    },
  };
}
